import { z } from 'zod';
import { newId } from '../../domain/id.ts';
import {
  StockLocationKindSchema,
  StockLocationSchema,
  type StockLocation,
} from '../../domain/entities/stock-location.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';

/**
 * The places a company keeps stock (INV2-06, SCRUM-268).
 *
 * A collection rather than free text on the item, because a filter
 * needs something to group by and a typo must not create a phantom
 * warehouse. The same argument the chart of accounts makes.
 *
 * **Nothing deletes one.** Items point at it, and a deleted location
 * leaves a rack number with no building — the argument `cost-code.ts`
 * makes at length. `retireStockLocation` sets `active: false`, which
 * keeps every item that points at it readable and stops it being
 * offered for new ones.
 */
export class StockLocationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StockLocationError';
  }
}

export const SaveStockLocationInputSchema = z.object({
  name: z.string().trim().min(1, 'Give the place a name').max(80),
  kind: StockLocationKindSchema.optional(),
  jobId: z.string().min(1).optional(),
});
export type SaveStockLocationInput = z.input<typeof SaveStockLocationInputSchema>;

export interface StockLocationContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

const repo = (context: { data: DataPort; companyId: string }) =>
  context.data.collection<StockLocation>({ companyId: context.companyId }, 'stockLocations');

export async function listStockLocations(context: {
  data: DataPort;
  companyId: string;
}): Promise<StockLocation[]> {
  const page = await repo(context).list();
  return [...page.items];
}

export function subscribeToStockLocations(
  context: { data: DataPort; companyId: string },
  onChange: (locations: readonly StockLocation[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe(undefined, (page) => onChange(page.items), onError);
}

/**
 * Two places with the same name are one place to everybody but a
 * database, so the second is refused rather than created.
 */
function nameKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export async function createStockLocation(
  context: StockLocationContext,
  input: SaveStockLocationInput,
): Promise<StockLocation> {
  const parsed = SaveStockLocationInputSchema.safeParse(input);
  if (!parsed.success) throw new StockLocationError(parsed.error.issues[0]!.message);

  const existing = await listStockLocations(context);
  if (existing.some((one) => nameKey(one.name) === nameKey(parsed.data.name))) {
    throw new StockLocationError('There is already a place with that name.');
  }

  const at = context.clock.now().toISOString();
  const location = StockLocationSchema.parse({
    id: newId(),
    companyId: context.companyId,
    name: parsed.data.name,
    ...(parsed.data.kind ? { kind: parsed.data.kind } : {}),
    ...(parsed.data.jobId ? { jobId: parsed.data.jobId } : {}),
    createdAt: at,
    createdBy: context.uid,
    updatedAt: at,
    updatedBy: context.uid,
  });
  await repo(context).create(location);
  return location;
}

export async function renameStockLocation(
  context: StockLocationContext,
  id: string,
  name: string,
): Promise<void> {
  const parsed = SaveStockLocationInputSchema.pick({ name: true }).safeParse({ name });
  if (!parsed.success) throw new StockLocationError(parsed.error.issues[0]!.message);

  const existing = await listStockLocations(context);
  if (existing.some((one) => one.id !== id && nameKey(one.name) === nameKey(parsed.data.name))) {
    throw new StockLocationError('There is already a place with that name.');
  }

  await repo(context).update(id, {
    name: parsed.data.name,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  });
}

/**
 * Out of use, not gone.
 *
 * An item that points at a retired location still says where it was
 * last kept, which is a better answer than a blank — somebody walking
 * to a closed yard learns something; somebody reading nothing learns
 * that the record is broken.
 */
export async function retireStockLocation(
  context: StockLocationContext,
  id: string,
): Promise<void> {
  await repo(context).update(id, {
    active: false,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  });
}
